import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ChakraProvider } from '@chakra-ui/react';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  ApiProvider,
  CookieProvider,
  getDevice,
  getQueryClient,
  I18nProvider,
  LanguageProvider,
  Locale,
  PathConfigProvider,
  RouterProvider,
  TagProvider,
  useVersionLogging,
} from '@ae/shared';
import { CSSReset } from '@ae/shared-comp';
import { Fonts, MediaProvider, sharedTheme } from '@ae/shared-ui';
import '../i18n';
import { standaloneComponentProps } from '../types';

export function useUrlChangeDetection(setPathname: (pathname: string) => void) {
  useEffect(() => {
    const url = window.location.href;

    const handleClick = () => {
      requestAnimationFrame(() => {
        if (url !== window.location.href) {
          setPathname(new URL(window.location.href).pathname);
        }
      });
    };

    document.body.addEventListener('click', handleClick, true);

    return () => {
      document.body.removeEventListener('click', handleClick, true);
    };
  }, [setPathname]);
}

/**
 * The `StandaloneComponent` is a wrapper component that provides various context providers
 * and configurations for the application. It sets up the environment for the child components
 * to function correctly, including internationalization, API access, authentication, and more.
 *
 * @param {string} appName - The name of the application.
 * @param {React.ReactNode} children - The child elements to render within the component.
 *
 * @returns {React.ReactElement} The rendered component with all the necessary providers.
 */
export function StandaloneComponent({
  appName,
  children,
}: {
  appName: string;
} & React.PropsWithChildren) {
  const url = new URL(window.location.href);
  const port = url.port ? `:${url.port}` : '';

  const [queryClient] = useState(() => getQueryClient());
  const { i18n } = useTranslation();
  const [pathname, setPathname] = useState(url.pathname);

  useVersionLogging(appName);
  useUrlChangeDetection(setPathname);

  const { assetsPath, apiEndpoint, supportedLngs, onLocaleChange } =
    standaloneComponentProps;

  // --- Context values ---

  return (
    <PathConfigProvider assetsPath={assetsPath} apiEndpoint={apiEndpoint}>
      <LanguageProvider
        locale={i18n.language as Locale}
        supportedLngs={supportedLngs}
        changeLocale={onLocaleChange}
      >
        <MediaProvider
          userDevice={getDevice(navigator.userAgent.toLowerCase())}
        >
          <CookieProvider value={document.cookie}>
            <I18nProvider useTranslation={useTranslation} Trans={Trans}>
              <ChakraProvider theme={sharedTheme} resetCSS={false}>
                <CSSReset />
                <QueryClientProvider client={queryClient}>
                  <RouterProvider
                    linkLegacyBehavior
                    protocol={url.protocol.replace(':', '')}
                    pathname={pathname}
                    hostname={`${url.hostname}${port}`}
                    query={url.search.slice(1)}
                    goBack={window.history.back}
                  >
                    <ApiProvider>
                      <TagProvider>
                        <>
                          <Fonts />
                          {children}
                        </>
                      </TagProvider>
                    </ApiProvider>
                  </RouterProvider>
                </QueryClientProvider>
              </ChakraProvider>
            </I18nProvider>
          </CookieProvider>
        </MediaProvider>
      </LanguageProvider>
    </PathConfigProvider>
  );
}
